import * as React from "react";
import * as React44ms from "../../../Util/form-utils/react-44ms";

import { useKeyboardNavigableList } from "../../hooks";

import { InputContainer2023, InputContainer2023Props } from "./input-container";

export interface SelectWithDropdownInput2023_Option {
  id: any;
  presentableLabel: string;
}

export interface SelectWithDropdownInput2023Expects {
  value: string;
  error?: string;
  required?: boolean;
}

export interface SelectWithDropdownInput2023Props
  extends Omit<InputContainer2023Props, "name"> {
  className?: string;

  options: SelectWithDropdownInput2023_Option[];

  placeholder?: string;

  formRef: React44ms.Api.TypedRef<SelectWithDropdownInput2023Expects>;
}
export const SelectWithDropdownInput2023: React.FunctionComponent<
  SelectWithDropdownInput2023Props
> = (props) => {
  const [formState] = React44ms.React.useForm(props.formRef);
  const { required, error } = formState || {};

  const name = React.useMemo(() => {
    return React44ms.React.Presentation.elements.nameFromRef(props.formRef);
  }, [props.formRef]);

  const value = React.useMemo(() => {
    const literalValue = formState?.value || "";
    const correspondingOption = props.options.find(
      (option) => option.id === literalValue
    );
    const presentableValue = correspondingOption?.presentableLabel || "";
    return presentableValue;
  }, [formState?.value, props.options]);
  const shouldPlaceholderBeShowing = React.useMemo(
    () => !value || value.length < 1,
    [value]
  );

  const [dropdownOpenState, setDropdownOpenState] = React.useState(false);
  const inputRef = React.useRef<HTMLParagraphElement | null>(null);
  const decorationRef = React.useRef<HTMLDivElement | null>(null);
  const dropdownRef = React.useRef<HTMLUListElement | null>(null);

  const shouldTheDropdownBeOpen = React.useMemo(
    () => dropdownOpenState && props.options.length > 0,
    [dropdownOpenState, props.options]
  );

  const handleBlur = React.useCallback(() => {
    setDropdownOpenState(false);
    inputRef.current?.blur();
    decorationRef.current?.blur();
    dropdownRef.current?.blur();

    React44ms.React.Event.dispatch(
      React44ms.React.Convenient.input.onBlur,
      props.formRef,
      null
    );
  }, [props.formRef]);

  const handleSelectOption = React.useCallback(
    (option: SelectWithDropdownInput2023_Option) => {
      React44ms.React.Event.dispatch(
        React44ms.React.Convenient.input.onChange,
        props.formRef,
        option.id
      );
      handleBlur();
    },
    [props.formRef]
  );

  const [activeSuggestionIndex, setActiveSuggestionIndex] =
    useKeyboardNavigableList({
      myList: props.options,
      canInteract: shouldTheDropdownBeOpen,
      handleSelect: handleSelectOption,
    });

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownOpenState &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        decorationRef.current &&
        !decorationRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpenState(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [dropdownOpenState]);

  const handleClickOfTheMainInputControl_whichShouldToggleTheDropdownOpenState: React.MouseEventHandler<HTMLInputElement> =
    React.useCallback(
      (event) => {
        event.preventDefault();
        setDropdownOpenState(!dropdownOpenState);
        React44ms.React.Event.dispatch(
          React44ms.React.Convenient.input.onFocus,
          [props.formRef],
          null
        );
      },
      [dropdownOpenState, setDropdownOpenState]
    );

  const handleClickOnAnOption = React.useCallback(
    (
      event: Parameters<React.MouseEventHandler<HTMLLIElement>>[0],
      optionSelected: SelectWithDropdownInput2023_Option
    ) => {
      event.preventDefault();
      handleSelectOption(optionSelected);
    },
    [handleSelectOption]
  );

  const handleHoverEnterOnAnOption = React.useCallback(
    (
      event: Parameters<React.MouseEventHandler<HTMLLIElement>>[0],
      optionHovering: SelectWithDropdownInput2023_Option,
      index: number
    ) => {
      setActiveSuggestionIndex(index);
    },
    [setActiveSuggestionIndex]
  );

  const handleMouseDownOnAnOption = React.useCallback(
    (event: Parameters<React.MouseEventHandler<HTMLLIElement>>[0]) => {
      // need to prevent the div from acquiring focus on mouse down
      event.preventDefault();
    },
    []
  );

  if (!name) {
    return null;
  }

  return (
    <InputContainer2023
      {...props}
      name={name}
      errorValueIfAny={error}
      required={required}
      className={`select-with-dropdown-input ${props?.className || ""}`}
    >
      <div className="input-immediate-container">
        <p
          ref={inputRef}
          className={`pretend-this-is-an-input ${
            shouldPlaceholderBeShowing
              ? "placeholder-version"
              : "not-a-placeholder-version"
          }`}
          onClick={
            handleClickOfTheMainInputControl_whichShouldToggleTheDropdownOpenState
          }
        >{`${!shouldPlaceholderBeShowing ? value : props.placeholder}`}</p>

        <div
          ref={decorationRef}
          className={`decoration-container ${
            shouldTheDropdownBeOpen ? "open" : "closed"
          }`}
          onClick={
            handleClickOfTheMainInputControl_whichShouldToggleTheDropdownOpenState
          }
        ></div>

        {shouldTheDropdownBeOpen && (
          <ul ref={dropdownRef} className="the-dropdown">
            {props.options.map((option, index) => (
              <li
                key={option.id}
                className={`option ${
                  index === activeSuggestionIndex ? "active" : "inactive"
                }`}
                onMouseEnter={(event) =>
                  handleHoverEnterOnAnOption(event, option, index)
                }
                onMouseDown={(event) => handleMouseDownOnAnOption(event)}
                onClick={(event) => handleClickOnAnOption(event, option)}
              >
                <p>{`${option.presentableLabel}`}</p>
              </li>
            ))}
          </ul>
        )}

        {props?.children}
      </div>
    </InputContainer2023>
  );
};
